import React from 'react';
import {Alert, Box, Chip, Divider, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {useSession} from '../../../../../firebase/UserProvider';
import {inferSourceFileType} from './sourcesAdapter';
import {fetchSourceFile, getRawStreamUrl} from './sourcesApi';
import SourcesFileRenderer from './SourcesFileRenderer';
import EntityTagsSection, {TAGS_SECTION_HEADING_SX} from '../../../../collection-requests/tags/EntityTagsSection';
import {formatDate} from '../../../../../utils/formatTimestamp';
import {getClassificationByValue} from '../../../../../components/prosemirror-editor/helpers/classificationLevels';
import useSourcesFileViewerStore from '../../../../../stores/useSourcesFileViewerStore';
import SourcesEditClassificationDialog from './SourcesEditClassificationDialog';

/**
 * Read-only chip list for one classification marking group (Distribution or Compartment).
 * Renders an explicit "Not set" placeholder when the group is empty so the field stays visible
 * next to Classification Level (the "Edit classification" dialog also allows an empty selection).
 * The placeholder is italic and deliberately not a chip — a plain "None" reads like an actual
 * marking value, whereas "Not set" makes clear no marking has been assigned.
 */
function ClassificationTagList({tags}) {
    const list = Array.isArray(tags) ? tags.filter(Boolean) : [];
    if (list.length === 0) {
        return (
            <Typography variant="body2" color="text.disabled" sx={{fontStyle: 'italic'}}>
                Not set
            </Typography>
        );
    }
    return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" alignSelf="flex-start">
            {list.map((tag) => (
                <Chip
                    key={tag}
                    size="small"
                    variant="outlined"
                    label={tag}
                    // Same metrics as the Classification Level chip so all three fields line up.
                    sx={{height: 20, minWidth: 28, '& .MuiChip-label': {px: 0.75}}}
                />
            ))}
        </Stack>
    );
}

function DataItemMetaHeader({fileDetails, fileSource, fileId}) {
    const {user} = useSession() || {};
    const userId = user?.uid || null;
    const [classificationDialogOpen, setClassificationDialogOpen] = React.useState(false);
    if (!fileDetails) return null;
    // For collection materials the document h1 (injected by MinerU) is the authoritative title —
    // showing fileDetails.name here would duplicate it. Suppress the name row in that case.
    const showName = fileSource !== 'collection_material';
    const url = fileDetails.meta?.url || fileDetails.url || null;
    const streamId = fileDetails.referencedDataItemId
        ? String(fileDetails.referencedDataItemId)
        : (fileDetails.id ? String(fileDetails.id) : null);
    const downloadUrl = streamId && userId
        ? getRawStreamUrl({referenceId: streamId, userId, disposition: 'attachment'})
        : null;
    const editFileId = fileId || streamId;
    const classificationLevel = fileDetails.classification?.level || null;
    const classificationDistribution = fileDetails.classification?.distribution || [];
    const classificationCompartment = fileDetails.classification?.compartment || [];
    const levelInfo = classificationLevel ? getClassificationByValue(classificationLevel) : null;
    const fields = [
        {key: 'created', label: 'Created', value: formatDate(fileDetails.createdAt)},
        {key: 'updated', label: 'Updated', value: formatDate(fileDetails.updatedAt)},
        ...(fileDetails.source ? [{key: 'source', label: 'Source', value: fileDetails.source}] : []),
        ...(fileDetails.mimeType ? [{key: 'type', label: 'Type', value: fileDetails.mimeType}] : []),
        // Classification is shown as three separate read-only fields — Classification Level,
        // Distribution and Compartment — mirroring the three inputs of the "Edit classification"
        // dialog (ClassificationFieldsGroup) so what is displayed maps 1:1 to what is editable.
        ...(classificationLevel ? [
            {
                key: 'classification-level',
                label: 'Classification Level',
                value: (
                    <Chip
                        size="small"
                        label={levelInfo ? levelInfo.value : classificationLevel}
                        sx={{
                            height: 20,
                            // The field wrapper is a column flex container, so without an explicit
                            // alignSelf the chip stretches to the full column width (a ~330px bar
                            // around a 1-character label). Hug the content and keep equal padding
                            // on both sides so short codes like "U" render as a balanced pill.
                            alignSelf: 'flex-start',
                            minWidth: 28,
                            fontWeight: 700,
                            color: levelInfo?.color,
                            bgcolor: levelInfo?.bgColor,
                            '& .MuiChip-label': {px: 0.75},
                        }}
                    />
                ),
                isNode: true,
            },
            {
                key: 'classification-distribution',
                label: 'Distribution',
                value: <ClassificationTagList tags={classificationDistribution}/>,
                isNode: true,
            },
            {
                key: 'classification-compartment',
                label: 'Compartment',
                value: <ClassificationTagList tags={classificationCompartment}/>,
                isNode: true,
            },
        ] : []),
    ].filter((f) => f.value);

    return (
        <Stack spacing={1.5} p={2} bgcolor="#F9F9F9" borderRadius={1} mx={2} mt={2} flexShrink={0}>
            {/* Title row: name (left) + download (right) */}
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Stack spacing={0.5} flex={1} minWidth={0}>
                    {showName && (
                        <Typography variant="subtitle2" sx={{fontWeight: 700, wordBreak: 'break-word'}}>
                            {fileDetails.name || 'File'}
                        </Typography>
                    )}
                    {url && (
                        <Typography
                            variant="caption"
                            component="a"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            noWrap
                            sx={{color: 'primary.main', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis'}}
                        >
                            {url}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" spacing={0.25} flexShrink={0} sx={{mt: -0.5}}>
                    {editFileId && (
                        <Tooltip title="Edit classification" arrow placement="top">
                            <IconButton
                                size="small"
                                aria-label="Edit classification"
                                onClick={() => setClassificationDialogOpen(true)}
                            >
                                <EditOutlinedIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                    {downloadUrl && (
                        <Tooltip title="Download" arrow placement="top">
                            <IconButton
                                size="small"
                                aria-label="Download file"
                                href={downloadUrl}
                                component="a"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <DownloadOutlinedIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>

            {fields.length > 0 && (
                <>
                    <Divider sx={{my: 0}}/>
                    <Stack direction="row" flexWrap="wrap" useFlexGap rowGap={1.25}>
                        {fields.map(({key, label, value, isNode}) => (
                            <Stack key={key} spacing={0.25} sx={{flex: 1, minWidth: 100}}>
                                <Typography sx={TAGS_SECTION_HEADING_SX}>{label}</Typography>
                                {isNode ? value : (
                                    <Typography variant="body2" noWrap>{value}</Typography>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                </>
            )}

            <Divider sx={{my: 0}}/>
            <EntityTagsSection
                entityType="DATA_ITEM"
                entityId={streamId}
                canEdit
                enabled={Boolean(streamId)}
                dense
            />
            <SourcesEditClassificationDialog
                open={classificationDialogOpen}
                onClose={() => setClassificationDialogOpen(false)}
                file={{id: editFileId, name: fileDetails.name || 'File'}}
                userId={userId}
            />
        </Stack>
    );
}


function normalizeSourceFilePayload(payload, fallbackFile) {
    const item = payload?.item || payload?.data || payload?.result || payload || {};
    const fileName = item?.name || item?.fileName || item?.filename || item?.title || fallbackFile?.fileName || null;
    const fileSource = item?.source || item?.docSource || item?.docSourceType || item?.classificationLevel || fallbackFile?.fileSource || null;
    const mimeType = item?.mimeType || item?.contentType || fallbackFile?.mimeType || null;
    const classification = item?.classification || null;

    return {
        ...item,
        id: item?.id || item?.itemId || item?.docId || fallbackFile?.fileId || null,
        referencedDataItemId: item?.referencedDataItemId || null,
        name: fileName,
        source: fileSource,
        mimeType,
        fileType: inferSourceFileType({...item, name: fileName, mimeType}),
        size: item?.size ?? item?.sizeBytes ?? item?.fileSize ?? null,
        createdAt: item?.createdAt ?? item?.created_at ?? null,
        updatedAt: item?.updatedAt ?? item?.updated_at ?? null,
        publishDate: item?.publishDate ?? item?.publish_date ?? null,
        status: item?.status ?? null,
        meta: item?.meta ?? null,
        classification,
        processingStage: item?.meta?.processing?.stage ?? null,
        processingError: item?.meta?.processing?.errorMessage ?? null,
    };
}

/**
 * Loads a My Files data-item and renders it via {@link SourcesFileRenderer}.
 *
 * Extracted from {@link SourcesFileViewerModal} so the same file viewer can render either
 * inside the standalone file-viewer modal OR inside the Source Package overlay center pane
 * (federated search now opens My Files in the same window as Corpus).
 */
const SourcesFileViewerContent = ({
    reportId = null,
    fileId,
    canonicalFileId = null,
    fileName = null,
    fileType = 'unknown',
    fileSource = null,
    sentenceHit = null,
    active = true,
    savedHighlights = null,
    activeHighlightSegmentIds = null,
    activeHighlightRanges = null,
    onCreateHighlight = null,
    searchMatches = null,
    activeSearchMatchIdx = -1,
    onPmContentReady = null,
    onDualPaneChange = null,
    hideToolbar = false,
}) => {
    const {user} = useSession() || {user: null};
    const [fileDetails, setFileDetails] = React.useState(null);
    const [isLoadingFile, setIsLoadingFile] = React.useState(() => active && Boolean(fileId));
    const [fileError, setFileError] = React.useState(null);

    React.useEffect(() => {
        if (!active || !fileId || !user?.uid) {
            setFileDetails(null);
            setIsLoadingFile(false);
            setFileError(null);
            return undefined;
        }

        const abortController = new AbortController();
        setFileDetails(null);
        setIsLoadingFile(true);
        setFileError(null);

        fetchSourceFile({fileId, userId: user.uid, signal: abortController.signal})
            .then((payload) => {
                setFileDetails(normalizeSourceFilePayload(payload, {fileId, fileName, fileType, fileSource}));
            })
            .catch((error) => {
                if (error?.name === 'AbortError') {
                    return;
                }
                console.error('[SourcesFileViewerContent] Failed to load source file:', error);
                setFileDetails(null);
                setFileError(error?.message || 'Failed to load source file.');
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setIsLoadingFile(false);
                }
            });

        return () => abortController.abort();
    }, [active, fileId, fileName, fileSource, fileType, user?.uid]);

    // Live-patch the open viewer when Edit classification (document header) saves a new
    // Classification Level/Distribution/Compartment for the file currently shown here. Avoids a
    // stale header until the user manually closes/reopens the viewer. See
    // SourcesEditClassificationDialog's notifyClassificationUpdated() call.
    const classificationUpdate = useSourcesFileViewerStore((s) => s.classificationUpdate);
    React.useEffect(() => {
        if (!classificationUpdate || !fileId) {
            return;
        }
        if (String(classificationUpdate.fileId) !== String(fileId)) {
            return;
        }
        setFileDetails((prev) => (prev ? {...prev, classification: classificationUpdate.classification} : prev));
    }, [classificationUpdate, fileId]);

    const activeFile = React.useMemo(() => ({
        fileId,
        canonicalFileId,
        fileName,
        fileType,
        fileSource,
    }), [canonicalFileId, fileId, fileName, fileSource, fileType]);

    if (!user?.uid) {
        return (
            <Stack flex={1} p={2}>
                <Alert severity="warning">An authenticated user is required to view files.</Alert>
            </Stack>
        );
    }

    const showMetaHeader = fileSource !== 'collection_material';

    return (
        <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            {showMetaHeader && (
                <DataItemMetaHeader fileDetails={fileDetails} fileId={fileId}/>
            )}
            <SourcesFileRenderer
                reportId={reportId}
                file={activeFile}
                fileDetails={fileDetails}
                isLoading={isLoadingFile}
                error={fileError}
                sentenceHit={sentenceHit}
                savedHighlights={savedHighlights}
                activeHighlightSegmentIds={activeHighlightSegmentIds}
                activeHighlightRanges={activeHighlightRanges}
                onCreateHighlight={onCreateHighlight}
                searchMatches={searchMatches}
                activeSearchMatchIdx={activeSearchMatchIdx}
                onPmContentReady={onPmContentReady}
                onDualPaneChange={onDualPaneChange}
                hideToolbar={hideToolbar || showMetaHeader}
            />
        </Box>
    );
};

export default React.memo(SourcesFileViewerContent);
export {normalizeSourceFilePayload};
